import { LootEntry } from './monsters.config';
import type { ModifierDropFilter } from '../types/modifier.types';
import { MODIFIER_REGISTRY } from './modifiers.config';
import { ITEMS } from './inventory.config';
import { WeightedEntry, WeightedPool } from '../types/loot.types';
import { rollWeightedPool } from '../utils/loot-roll.utils';

/**
 * Named loot pools for Location drops.
 * map-loot mirrors MAP_LOOT_TABLE from monsters.config.ts.
 *
 * Trinkets, sketches and intra-run modifiers intentionally do NOT appear in
 * these random pools: trinkets are unlocked via designated sources (tutorial
 * / chapter bosses, fixed locations, major level-ups), modifiers live in
 * `MODIFIER_REGISTRY` (drawn via `MODIFIER_DROP_FILTERS` below), and
 * sketches drop as signature items wired through monster `lootTable`s.
 * cf. docs/game-design/loot.md.
 */
/**
 * System 1 pools (independent per-entry chance + random quantity).
 * Used by `chest`, `urn` (and monster loot tables share the same model).
 */
export const INDEPENDENT_LOOT_POOLS: Record<string, LootEntry[]> = {
  'map-loot': [
    { itemId: 'pill-life', chance: 0.3, quantityMin: 1, quantityMax: 2 },
    { itemId: 'pill-spirit', chance: 0.25, quantityMin: 1, quantityMax: 1 },
    { itemId: 'antidote', chance: 0.15, quantityMin: 1, quantityMax: 1 },
    {
      itemId: 'soul-shard',
      chance: 0.15,
      quantityMin: 1,
      quantityMax: 3,
    },
    {
      itemId: 'thunder-in-a-box',
      chance: 0.1,
      quantityMin: 1,
      quantityMax: 1,
    },
    { itemId: 'elixir-sharp', chance: 0.05, quantityMin: 1, quantityMax: 1 },
    { itemId: 'elixir-solid', chance: 0.05, quantityMin: 1, quantityMax: 1 },
    { itemId: 'elixir-swift', chance: 0.05, quantityMin: 1, quantityMax: 1 },
    {
      itemId: 'reviver-seed',
      chance: 0.03,
      quantityMin: 1,
      quantityMax: 1,
    },
    {
      itemId: 'escape-orb',
      chance: 0.01,
      quantityMin: 1,
      quantityMax: 1,
    },
    { itemId: 'key', chance: 0.05, quantityMin: 1, quantityMax: 1 },
  ],
};

/**
 * System 2 pools (X guaranteed weighted draws, one unit each, capped per
 * item by `max`). Used by `vault` (key chest) and `challenge-chest`.
 * Weights/maxes ported from the former chance-based pools (weight = chance
 * × 100, max = quantityMax) — tunable placeholders.
 */
export const WEIGHTED_LOOT_POOLS: Record<string, WeightedPool> = {
  treasure: {
    count: 3,
    entries: [
      { itemId: 'elixir-sharp', weight: 40, max: 1 },
      { itemId: 'elixir-solid', weight: 40, max: 1 },
      { itemId: 'elixir-swift', weight: 40, max: 1 },
      { itemId: 'spirit-heart', weight: 35, max: 2 },
      { itemId: 'thunder-in-a-box', weight: 30, max: 2 },
      { itemId: 'reviver-seed', weight: 20, max: 1 },
      { itemId: 'escape-orb', weight: 10, max: 1 },
    ],
  },
  'challenge-treasure': {
    count: 5,
    entries: [
      { itemId: 'pill-life', weight: 30, max: 2 },
      { itemId: 'pill-spirit', weight: 25, max: 1 },
      { itemId: 'antidote', weight: 15, max: 1 },
      { itemId: 'soul-shard', weight: 15, max: 3 },
      { itemId: 'thunder-in-a-box', weight: 10, max: 1 },
      { itemId: 'elixir-sharp', weight: 5, max: 1 },
      { itemId: 'elixir-solid', weight: 5, max: 1 },
      { itemId: 'elixir-swift', weight: 5, max: 1 },
      { itemId: 'reviver-seed', weight: 3, max: 1 },
      { itemId: 'escape-orb', weight: 1, max: 1 },
      { itemId: 'key', weight: 5, max: 1 },
    ],
  },
};

/**
 * Per-source filters for drawing from `MODIFIER_REGISTRY`.
 * cf. Content Bible v1 §8 distribution. Weighted draw happens in the drop
 * pipeline (relative common/rare/epic weights).
 *
 * Convention:
 * - `chest-common`        : common chests (5 common + 2 rare).
 * - `altar`               : altar boons (2 rare + 1 epic).
 * - `boss-signature`      : mini-boss / boss (rare or epic, 50/50 guaranteed).
 * - `grimoire-shrine`  : grimoire shrine (5 common + 2 rare).
 * - `challenge`           : challenge-room reward (rare or legendary, guaranteed).
 */
export const MODIFIER_DROP_FILTERS: Record<string, ModifierDropFilter> = {
  'chest-common': {
    types: ['small', 'impactful'],
    rarities: ['common', 'rare'],
  },
  altar: {
    types: ['impactful'],
    rarities: ['rare', 'legendary'],
  },
  'boss-signature': {
    types: ['impactful'],
    rarities: ['rare', 'legendary'],
  },
  'grimoire-shrine': {
    types: ['small', 'impactful'],
    rarities: ['common', 'rare'],
  },
  challenge: {
    types: ['impactful'],
    rarities: ['rare', 'legendary'],
  },
};

const RARITY_WEIGHTS: Record<string, number> = {
  common: 5,
  rare: 2,
  legendary: 1,
};

/**
 * Builds the weighted modifier pool for a drop source: eligible modifiers
 * from `MODIFIER_REGISTRY` filtered by `MODIFIER_DROP_FILTERS[source]`,
 * weighted by rarity, one copy max each. Consumed by `rollWeightedPool`.
 */
export function buildModifierPoolEntries(
  source: string | undefined,
): WeightedEntry[] {
  const filter = source ? MODIFIER_DROP_FILTERS[source] : undefined;
  return Object.values(MODIFIER_REGISTRY)
    .filter(
      (mod) =>
        !filter ||
        (filter.types.includes(mod.type) &&
          filter.rarities.includes(mod.rarity)),
    )
    .map((mod) => ({
      itemId: `boost-${mod.id}`,
      weight: RARITY_WEIGHTS[mod.rarity] ?? 1,
      max: 1,
    }))
    .filter((e) => ITEMS[e.itemId]);
}

export function rollBoostItemId(source: string | undefined): string | null {
  const entries = buildModifierPoolEntries(source);
  if (entries.length === 0) {
    return null;
  }
  return rollWeightedPool(entries, 1)[0]?.itemId ?? null;
}

/**
 * 22 common grimoires (one per non-starter, non-signature spell).
 * Consumed by the `spawnRandomGrimoire` handler (Grimoire Sanctuary effect)
 * which uniformly picks an id and drops it on the floor.
 * If the spell is already unlocked in `useSpellCatalogueStore`, the
 * `unlockSpell` handler grants mastery XP fragments instead (fallback
 * handled in `unlockSpell`).
 *
 * Starters (outburst, startle) and signatures (euphoria, despair, wrath,
 * terror, ambush, plague) are excluded — signatures have grimoires but are
 * obtained through dedicated sources, not random shrine drops.
 */
export const GRIMOIRE_POOL: string[] = [
  'grimoire-joyful-rush',
  'grimoire-radiance',
  'grimoire-warm-ward',
  'grimoire-cheer',
  'grimoire-tear-line',
  'grimoire-gloom',
  'grimoire-heavy-heart',
  'grimoire-downpour',
  'grimoire-reckless-blow',
  'grimoire-eruption',
  'grimoire-charge',
  'grimoire-fear-strike',
  'grimoire-shadow-snipe',
  'grimoire-paralyze',
  'grimoire-creeping-dread',
  'grimoire-blink-strike',
  'grimoire-gale-cone',
  'grimoire-far-shot',
  'grimoire-venom-bite',
  'grimoire-decay-cloud',
  'grimoire-drain',
  'grimoire-repulse',
];
